import type { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { Navigate, useParams } from 'react-router'
import { AmountCard, Checkbox, ExplainerBanner, FlowLayout, Icon, Sheet, Skeleton } from '@/ds'
import { formatMoney, time } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { isServiceError } from '@/services/errors'
import { reservaService } from '@/services/reserva'
import type { ChangeInput, ChangeQuote, ChangeType, Stay } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { plainText } from '../Pagos/shared'
import { BOOKING_ROUTES, dayOf, longRange, quoteLines, readDraft, signed, TOTAL_CHANGE_STEPS, TYPE_BY_SLUG, useBookingStay, useLang, writeDraft, writeResult } from './shared'

/** Confirmación verde de §5c: "El ático está libre del 4 al 7 de octubre" y sus equivalentes en los otros cambios. */
function availableText(quote: ChangeQuote, stay: Stay, lang: string, t: TFunction): string {
  const { input } = quote
  switch (input.type) {
    case 'dates':
    case 'nights':
      return t('v2.reserva.precio.libre', { unit: stay.unit.name, range: longRange(input.checkIn, input.checkOut, lang, t) })
    case 'unit':
      return t('v2.reserva.precio.libre', { unit: quote.unitName ?? '', range: longRange(dayOf(stay.dates.checkIn), dayOf(stay.dates.checkOut), lang, t) })
    case 'early_checkin':
      return t('v2.reserva.precio.entradaPosible', { time: input.time })
    case 'late_checkout':
      return t('v2.reserva.precio.salidaPosible', { time: input.time })
    case 'guests':
      return t('v2.reserva.precio.posible')
  }
}

export const differenceLabel = (amount: number, t: TFunction) => (amount > 0 ? t('v2.reserva.precio.diferencia') : amount < 0 ? t('v2.reserva.precio.aFavor') : t('v2.reserva.precio.sinDiferencia'))

/** Mensaje de un cambio que no sale al confirmar. */
export function changeErrorMessage(e: unknown, t: TFunction): string {
  if (isServiceError(e) && e.code === 'offline') return t('v2.ds.offline')
  if (isServiceError(e) && e.code === 'quote_expired') return t('v2.reserva.pagar.caducada')
  return t('v2.legacy.error.Error_has_ocurred_try_again_or_contact_us')
}

/**
 * 8.5 · Paso 2 de un cambio: disponibilidad y precio en tiempo real (Reserva.dc.html screen=price, §5c). Fechas y
 * noches contra E11; el resto, mock. Diferencia con desglose, porqué y condiciones aplicables. Con coste, al paso de
 * pago; sin coste o a favor, confirmación con E12 como la app actual, sin prometer devolución (D45).
 */
export function CambiarPrecio() {
  const { tipo = '' } = useParams()
  const type = TYPE_BY_SLUG[tipo]
  const stay = useBookingStay()
  const { search } = useAppNavigate()
  const [draft] = useState(() => (type ? readDraft(stay.guestCode, type) : null))
  if (!type) return <Navigate to={withQuery(BOOKING_ROUTES.detail, search)} replace />
  if (!draft?.input) return <Navigate to={withQuery(BOOKING_ROUTES.pick(type), search)} replace />
  return <Quote stay={stay} type={type} input={draft.input} />
}

function Quote({ stay, type, input }: { stay: Stay; type: ChangeType; input: ChangeInput }) {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const { go } = useAppNavigate()
  const [quote, reload] = useAsync(`cotizar:${stay.guestCode}:${JSON.stringify(input)}`, (signal) => reservaService.quoteChange(stay, input, apiLang, signal))
  const [accepted, setAccepted] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [conditions, setConditions] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const q = quote.kind === 'ready' ? quote.value : null

  // Sin disponibilidad o sin endpoint: "No podemos hacer ese cambio" (§5g) con lo que se sabe.
  useEffect(() => {
    if (quote.kind === 'unsupported') {
      writeDraft({ guestCode: stay.guestCode, type, input, quote: null, reason: 'property' })
      go(BOOKING_ROUTES.unavailable(type), { replace: true })
    } else if (q && !q.available) {
      writeDraft({ guestCode: stay.guestCode, type, input, quote: q, reason: q.busy ? 'busy' : 'rejected' })
      go(BOOKING_ROUTES.unavailable(type), { replace: true })
    }
    // go cambia en cada render; la redirección depende solo del resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.kind, q])

  const needsAcceptance = Boolean(q?.conditions)
  const amount = q ? formatMoney({ ...q.difference, amount: Math.abs(q.difference.amount) }, lang) : ''
  const dateTypes = type === 'dates' || type === 'nights'

  const toPayment = () => {
    if (!q) return
    setShowErrors(true)
    if (needsAcceptance && !accepted) return
    writeDraft({ guestCode: stay.guestCode, type, input, quote: q, reason: null })
    go(BOOKING_ROUTES.pay(type))
  }
  const openConfirm = () => {
    setShowErrors(true)
    if (needsAcceptance && !accepted) return
    setConfirm(true)
  }
  const apply = async () => {
    if (!q) return
    setSaving(true)
    setError(null)
    try {
      const res = await reservaService.confirmChange(stay, q, null, apiLang)
      if (res.status !== 'applied') throw new Error('unexpected payment without card')
      writeResult(stay.guestCode, res.result)
      writeDraft(null)
      go(BOOKING_ROUTES.done(type), { replace: true })
    } catch (e) {
      setConfirm(false)
      setError(changeErrorMessage(e, t))
      if (isServiceError(e) && e.code === 'quote_expired') reload()
    } finally {
      setSaving(false)
    }
  }

  let primary
  if (q?.available && q.confirmable && q.difference.amount > 0) primary = { label: t('v2.reserva.precio.continuarPagar', { amount }), onClick: toPayment }
  else if (q?.available && q.confirmable) primary = { label: t('v2.reserva.precio.confirmar'), onClick: openConfirm, loading: saving }

  return (
    <FlowLayout
      progress={{ step: 2, total: TOTAL_CHANGE_STEPS, backLabel: t('v2.comun.atras'), onBack: () => go(BOOKING_ROUTES.pick(type)), saved: false }}
      title={t('v2.reserva.precio.titulo')}
      intro={t('v2.reserva.precio.lead')}
      primary={primary}
      secondary={{ label: dateTypes ? t('v2.reserva.precio.otrasFechas') : t('v2.reserva.precio.otraOpcion'), onClick: () => go(BOOKING_ROUTES.pick(type)) }}
      footerMessage={
        error ? (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        ) : null
      }
    >
      {quote.kind === 'loading' || (q && !q.available) || quote.kind === 'unsupported' ? (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={56} />
          <Skeleton height={260} />
        </div>
      ) : quote.kind === 'error' ? (
        <SectionError code={quote.code} onRetry={reload} />
      ) : q ? (
        <>
          <div className="reserva__available" role="status">
            <Icon name="check-circle" size={24} />
            <span>{availableText(q, stay, lang, t)}</span>
          </div>
          <AmountCard
            total={signed(q.difference, lang)}
            totalLabel={differenceLabel(q.difference.amount, t)}
            lines={quoteLines(q, stay, lang, t)}
            totalRowLabel={q.difference.amount > 0 ? t('v2.reserva.precio.pagasAhora') : q.difference.amount < 0 ? t('v2.reserva.precio.aFavor') : t('v2.reserva.precio.total')}
            buttonLabel={null}
            note={q.difference.amount < 0 && q.refundEtaDays ? t('v2.reserva.precio.devolucion', { amount, min: q.refundEtaDays.min, max: q.refundEtaDays.max }) : undefined}
          />
          {q.reason && (
            <ExplainerBanner title={q.difference.amount > 0 ? t('v2.reserva.precio.porQueSube', { amount }) : q.difference.amount < 0 ? t('v2.reserva.precio.porQueBaja', { amount }) : t('v2.reserva.precio.porQueIgual')}>{q.reason}</ExplainerBanner>
          )}
          {q.expiresAt && <p className="pagos__note tnum">{t('v2.reserva.precio.caduca', { time: time(q.expiresAt, lang) })}</p>}
          {q.conditions && q.confirmable && (
            <div className="pagos__group">
              <Checkbox checked={accepted} onChange={setAccepted}>
                <Trans
                  i18nKey="v2.reserva.precio.aceptar"
                  components={{
                    2: (
                      <button
                        type="button"
                        className="pagos__link-button"
                        onClick={(e) => {
                          e.preventDefault()
                          setConditions(true)
                        }}
                      />
                    ),
                  }}
                />
              </Checkbox>
              {showErrors && !accepted && (
                <span className="pagos__error" role="alert">
                  {t('v2.legacy.preview.labels.modifyBookingView.consultSuccess.error')}
                </span>
              )}
            </div>
          )}
          <Sheet open={conditions} title={t('v2.legacy.payment.Conditions')} confirmHidden onCancel={() => setConditions(false)}>
            <span className="pagos__pre">{plainText(q.conditions ?? '')}</span>
          </Sheet>
          <Sheet
            open={confirm}
            title={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.modifyBooking.title')}
            confirmLabel={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.modifyBooking.continueButton')}
            cancelLabel={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.modifyBooking.cancelButton')}
            onConfirm={() => void apply()}
            onCancel={() => setConfirm(false)}
            loading={saving}
          >
            {t('v2.legacy.preview.labels.modifyBookingView.alertDialog.modifyBooking.description')}
          </Sheet>
        </>
      ) : null}
    </FlowLayout>
  )
}
